import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

type Sign = '<' | '=' | '>';

interface RoundConfig {
  random: boolean;
  numberOfSublevels: number;
  numberOfEquations: number;
  minValue: number;
  maxValue: number;
  maxDistanceBetweenNumbers?: number;
  precision?: number;
  sameInteger?: boolean;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: RoundConfig[];
}

interface LevelsFile {
  levels: Level[];
}

interface Pair {
  left: number;
  right: number;
  correct: Sign;
  chosen: Sign | null;
  state: 'pending' | 'correct' | 'wrong';
}

const LEVELS = (levelsData as LevelsFile).levels;
const SIGNS: ReadonlyArray<Sign> = ['<', '=', '>'];

export function mountComparator(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  let roundIndex = 0;
  let pairs: Pair[] = [];
  let selectedIdx = 0;
  let correctRoundsCount = 0;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Comparar números</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage comparator">
      <div class="feedback" aria-live="polite"></div>
      <p class="comparator__progress" aria-live="polite"></p>
      <p class="comparator__hint">Escolha o sinal correto pra cada par.</p>
      <div class="comparator__list" role="list"></div>
      <div class="comparator__signs" role="group" aria-label="Sinais de comparação">
        ${SIGNS.map((s) => `<button class="comparator__sign" type="button" data-sign="${s}" aria-label="${signLabel(s)}">${s}</button>`).join('')}
      </div>
      <button class="btn btn--primary comparator__ok" type="button">Confirmar</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const listEl = page.querySelector<HTMLElement>('.comparator__list')!;
  const progressEl = page.querySelector<HTMLElement>('.comparator__progress')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.comparator__ok')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function startRound(): void {
    const cfg = level.data[roundIndex]!;
    const n = cfg.numberOfEquations ?? 5;
    pairs = [];
    for (let i = 0; i < n; i++) {
      const { left, right } = genPair(cfg);
      pairs.push({ left, right, correct: correctSign(left, right), chosen: null, state: 'pending' });
    }
    selectedIdx = 0;
    renderProgress();
    renderList();
  }

  function renderProgress(): void {
    progressEl.textContent = `Rodada ${roundIndex + 1} de ${level.data.length}`;
  }

  function renderList(): void {
    listEl.innerHTML = '';
    pairs.forEach((p, i) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'comparator__row';
      row.setAttribute('role', 'listitem');
      row.dataset.idx = String(i);
      if (i === selectedIdx) row.classList.add('comparator__row--selected');
      if (p.state === 'wrong') row.classList.add('comparator__row--wrong');
      if (p.state === 'correct') row.classList.add('comparator__row--correct');
      const slotChar = p.chosen ?? '?';
      const filledClass = p.chosen ? ' comparator__slot--filled' : '';
      const resultIcon = p.state === 'wrong' ? '✗' : p.state === 'correct' ? '✓' : '';
      row.innerHTML = `
        <span class="comparator__num">${fmt(p.left)}</span>
        <span class="comparator__slot${filledClass}" aria-label="Sinal">${slotChar}</span>
        <span class="comparator__num">${fmt(p.right)}</span>
        <span class="comparator__result" aria-hidden="true">${resultIcon}</span>
      `;
      row.addEventListener('click', () => {
        selectedIdx = i;
        renderList();
      });
      listEl.appendChild(row);
    });
  }

  function chooseSign(sign: Sign): void {
    if (selectedIdx < 0 || selectedIdx >= pairs.length) return;
    const p = pairs[selectedIdx]!;
    p.chosen = sign;
    p.state = 'pending';
    ctx.audio.play('click');
    const nextEmpty = pairs.findIndex((x, i) => i > selectedIdx && x.chosen === null);
    if (nextEmpty >= 0) selectedIdx = nextEmpty;
    renderList();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 600);
  }

  function validate(): void {
    if (pairs.some((p) => p.chosen === null)) {
      showFeedback('err');
      ctx.audio.play('wrong');
      return;
    }
    let anyWrong = false;
    pairs.forEach((p) => {
      if (p.chosen === p.correct) p.state = 'correct';
      else { p.state = 'wrong'; anyWrong = true; }
    });
    if (!anyWrong) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctRoundsCount++;
      roundIndex++;
      renderList();
      window.setTimeout(() => {
        if (roundIndex < level.data.length) startRound();
        else finishLevel();
      }, 800);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      const firstWrong = pairs.findIndex((p) => p.state === 'wrong');
      if (firstWrong >= 0) selectedIdx = firstWrong;
      renderList();
    }
  }

  okBtn.addEventListener('click', validate);
  page.querySelectorAll<HTMLButtonElement>('.comparator__sign').forEach((btn) => {
    btn.addEventListener('click', () => chooseSign(btn.dataset.sign as Sign));
  });

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === '<' || e.key === ',') chooseSign('<');
    else if (e.key === '>' || e.key === '.') chooseSign('>');
    else if (e.key === '=') chooseSign('=');
    else if (e.key === 'Enter') validate();
    else if (e.key === 'ArrowUp') {
      if (selectedIdx > 0) { selectedIdx--; renderList(); }
    } else if (e.key === 'ArrowDown') {
      if (selectedIdx < pairs.length - 1) { selectedIdx++; renderList(); }
    } else {
      return;
    }
    e.preventDefault();
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    const totalRounds = level.data.length;
    const score = correctRoundsCount;
    ctx.onComplete(level.level, score);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctRoundsCount} de ${totalRounds} ${totalRounds === 1 ? 'rodada' : 'rodadas'}.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/comparator/${level.level + 1}`;
      });
    }
  }

  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

function signLabel(s: Sign): string {
  if (s === '<') return 'menor que';
  if (s === '>') return 'maior que';
  return 'igual';
}

function correctSign(left: number, right: number): Sign {
  if (Math.abs(left - right) < 1e-9) return '=';
  return left < right ? '<' : '>';
}

function fmt(n: number): string {
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

function genPair(cfg: RoundConfig): { left: number; right: number } {
  const precision = cfg.precision ?? 1;
  const min = cfg.minValue;
  const max = cfg.maxValue;
  const maxDist = cfg.maxDistanceBetweenNumbers;
  const digits = precision < 1 ? Math.max(0, Math.round(-Math.log10(precision))) : 0;

  let left: number;
  let right: number;

  if (cfg.sameInteger) {
    const intMin = Math.floor(min);
    const intMax = Math.floor(max);
    const integer = randInt(intMin, intMax);
    const fracSteps = Math.max(0, Math.round((1 - precision) / precision));
    left = integer + randInt(0, fracSteps) * precision;
    right = integer + randInt(0, fracSteps) * precision;
    if (left > max) left = integer;
    if (right > max) right = integer;
  } else {
    left = randomMultiple(min, max, precision);
    if (maxDist !== undefined) {
      const rightMin = Math.max(min, left - maxDist);
      const rightMax = Math.min(max, left + maxDist);
      right = randomMultiple(rightMin, rightMax, precision);
    } else {
      right = randomMultiple(min, max, precision);
    }
  }

  left = roundTo(left, digits);
  right = roundTo(right, digits);
  return { left, right };
}

function randomMultiple(min: number, max: number, step: number): number {
  if (step >= 1) return randInt(Math.ceil(min), Math.floor(max));
  const steps = Math.floor((max - min) / step + 1e-9);
  return min + randInt(0, Math.max(0, steps)) * step;
}

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function roundTo(n: number, digits: number): number {
  const f = Math.pow(10, digits);
  return Math.round(n * f) / f;
}
